/**
 * Card Component
 * 
 * Container component for grouping content.
 * Used primarily for question cards in the funnel.
 * 
 * Features:
 * - Consistent padding and border radius
 * - Subtle shadow for depth
 * - Hover state option for interactive cards
 * - Animation support with Framer Motion
 */

import React, { forwardRef, HTMLAttributes } from 'react';
import { motion, HTMLMotionProps, Variants } from 'framer-motion';

// ============================================
// TYPES
// ============================================

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  /** Enable hover animation */
  isHoverable?: boolean;
  /** Enable click animation */
  isClickable?: boolean;
  /** Remove padding */
  noPadding?: boolean;
  /** Custom animation variants */
  variants?: Variants;
  /** Children content */
  children: React.ReactNode;
}

// ============================================
// ANIMATION VARIANTS
// ============================================

/**
 * Default animation variants for card appearance
 */
const defaultVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94], // Ease out quart
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.98,
    transition: {
      duration: 0.3,
    },
  },
};

// ============================================
// COMPONENT
// ============================================

/**
 * Card component with optional animations
 * Wraps content in a styled container
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      isHoverable = false,
      isClickable = false,
      noPadding = false,
      variants,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    // Build class string
    const cardClasses = `
      bg-white
      rounded-2xl
      shadow-card
      ${noPadding ? '' : 'p-6 sm:p-8 md:p-12'}
      ${isHoverable ? 'hover:shadow-card-hover transition-shadow duration-200' : ''}
      ${isClickable ? 'cursor-pointer' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    // Motion props for animations
    const motionProps: HTMLMotionProps<'div'> = {
      variants: variants || defaultVariants,
      initial: 'hidden',
      animate: 'visible',
      exit: 'exit',
    };

    // Add hover/tap animations if interactive
    if (isHoverable || isClickable) {
      motionProps.whileHover = { scale: 1.01 };
    }
    if (isClickable) {
      motionProps.whileTap = { scale: 0.99 };
    }

    return (
      <motion.div
        ref={ref}
        className={cardClasses}
        {...motionProps}
        {...(props as HTMLMotionProps<'div'>)}
      >
        {children}
      </motion.div>
    );
  }
);

Card.displayName = 'Card';

// ============================================
// CARD HEADER SUBCOMPONENT
// ============================================

interface CardHeaderProps {
  /** Main heading text */
  title: string;
  /** Optional subtext/description */
  subtitle?: string;
  /** Optional step indicator */
  step?: string;
  /** Custom class name */
  className?: string;
}

/**
 * CardHeader - Consistent header styling for cards
 */
export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  step,
  className = '',
}) => {
  return (
    <div className={`mb-8 ${className}`}>
      {/* Step indicator */}
      {step && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm font-medium text-primary mb-2"
        >
          {step}
        </motion.p>
      )}
      
      {/* Main title */}
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-2xl sm:text-3xl md:text-4xl font-bold text-text-primary leading-tight font-heading"
      >
        {title}
      </motion.h2>
      
      {/* Subtitle/description */}
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-3 text-base sm:text-lg text-text-secondary"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
};

CardHeader.displayName = 'CardHeader';

// ============================================
// CARD FOOTER SUBCOMPONENT
// ============================================

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * CardFooter - Container for card actions
 */
export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className={`mt-8 flex flex-col sm:flex-row gap-3 ${className}`}
    >
      {children}
    </motion.div>
  );
};

CardFooter.displayName = 'CardFooter';

export default Card;
